import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, ShareNetwork, WarningCircle } from '@phosphor-icons/react';

import { MarketShell } from '../../../design-system/components/shells';
import {
  Button,
  Empty,
  QtyStepper,
} from '../../../design-system/components/desktop';
import { BuyBar, Cta } from '../../../design-system/components/mobile';
import { Icon } from '../../../design-system/components/Icon';
import { useViewport } from '../../../design-system/hooks/useViewport';
import { ListingCard, ListingGrid } from '../../../design-system/patterns/market';
import {
  BuyAffix,
  CtaNote,
  CtaStack,
  DetailAside,
  DetailCol,
  DetailLayout,
  DetailSection,
  DetailSkeleton,
  FieldLabel,
  Gallery,
  Identity,
  MarketLine,
  PriceBox,
  PriceHistory,
  PriceLead,
  Prose,
  QtyRow,
  SellerBox,
  SpecList,
  TrustRows,
} from '../../../design-system/patterns/detail';
import { formatCurrency } from '../../../utils/common';
import { distinguishingFinish, finishLabel } from '../../../utils/finish';
import { errorText, LOAD_FAILURE } from '../../../utils/errorText';

/**
 * ONE SELLER'S LISTING, `/marketplace/card/:cardId/:listId`. Pure view.
 *
 * THIS IS THE PAGE WITH THE BUY BOX, and the sibling catalog page deliberately
 * does not have one. A catalog page cannot close a sale because twelve sellers
 * hold twelve different copies at twelve prices; this page is one copy, one
 * price, one condition, one quantity, so the sale can close right here.
 *
 * The two pages used to be near-duplicate trees whose stylesheets fought over
 * the same global class names. They now share `patterns/detail` and nothing
 * else, and neither emits a class the other's CSS can reach.
 *
 * BUY BOX ORDER IS FIXED AND DOES NOT MOVE
 * ----------------------------------------
 *   identity, market price, price box, CTA stack, then specs and buyer
 *   protection.
 * Identity and market price are the two facts the buyer came for. The price
 * box sits directly under the market price and holds the price of this copy
 * with the price history under it. It used to be a two-tab strip, which meant
 * the price of this copy could be the panel that was not showing.
 *
 * THE MOBILE BUY BAR IS A REAL DOM DIFFERENCE, NOT A MEDIA QUERY
 * --------------------------------------------------------------
 * Below 1024 the primary action is a PINNED bottom bar; at and above it, a
 * sticky buy box in the right column. Rendering both and hiding one would put
 * two copies of "Add to cart" in the tab order and ship two handlers for one
 * job, so this branches on `useViewport()` and mounts exactly one.
 *
 * WHAT IS DELIBERATELY ABSENT
 * ---------------------------
 * No "Make an offer" and no "Message seller": the app has neither feature, and
 * a control that cannot work is worse than no control. No courier, no origin
 * city and no shipping price: shipping is nationwide and we cannot pull any of
 * the rest. No PSA / BGS / CGC grade, because there is no grade field.
 */

export function ListingDetailView({
  loading = false,
  error,
  notFound = false,

  listing = {},
  seller = {},
  priceHistory,

  quantity = 1,
  onQuantityChange,
  maxQuantity,
  inCartQuantity = 0,
  adding = false,
  onAddToCart,

  saved = false,
  saving = false,
  onToggleSave,
  onShare,
  shareModal,

  sellerListings = [],
  related = [],
  onCartClick,
  wishlistCount = 0,

  /* WHERE THE CARD PAGE ABOVE CAME FROM, handed down by it and handed straight
     back on every control that climbs. This page's own parent is never in
     doubt -- it is the card -- but that card's parent IS: opened out of a
     search it goes back to the results, opened off the browse grid it goes
     back to the marketplace. It cannot tell which unless the trip back up
     carries the answer, and this is the hop that would otherwise drop it. */
  backState,
  /* Positive knowledge that Sleevr can actually deliver, from
     `useFulfilmentMode` in the container. Defaults FALSE so the buy box makes
     no delivery promise it has not been told is true — the endpoint behind it
     is IsAuthenticated and this page is public. See `TrustRows`. */
  shipsNationwide = false,
}) {
  const { isDesktop } = useViewport();
  const [imageIndex, setImageIndex] = useState(0);

  const shell = {
    breadcrumbs: [
      { label: 'Marketplace', to: '/marketplace' },
      ...(listing.cardHref
        ? [{ label: listing.name || 'Card', to: listing.cardHref, state: backState }]
        : []),
      { label: seller.storeName || seller.name || 'Listing' },
    ],
    title: listing.name,
    /* Both ways up carry it, not just the button. A crumb that climbed to the
       card WITHOUT the origin would put the bug back on the other control --
       same page, same destination, and only one of them remembering. */
    back: listing.cardHref
      ? { to: listing.cardHref, label: 'All listings', state: backState }
      : undefined,
    onCartClick,
    wishlistCount,
    activeNav: 'marketplace',
  };

  if (loading) {
    return (
      <MarketShell {...shell}>
        {/* No seller table on this route, so no table block in its shape. */}
        <DetailSkeleton />
      </MarketShell>
    );
  }

  if (notFound || error) {
    return (
      <MarketShell {...shell}>
        <Empty
          icon={WarningCircle}
          illustration={notFound ? 'not-found' : 'request-failed'}
          tone={notFound ? 'default' : 'error'}
          title={notFound ? 'That listing is no longer available' : 'We could not load this listing'}
          text={
            notFound
              ? 'The seller may have removed it or sold the last copy. Other sellers may still have this card.'
              : errorText(error, LOAD_FAILURE)
          }
          actions={
            /* `as={Link} to=`, not `as="a" href=`. A plain anchor to an
               in-app route is a FULL PAGE LOAD: it tears down the React tree,
               refetches the bundle and drops everything the router was
               holding -- including the origin this subtree threads back up, so
               the way out of a dead listing also lost the search behind it.
               The `state` goes with it for the same reason every other control
               that climbs carries one; see `backState` above. */
            <Button
              as={Link}
              to={listing.cardHref || '/marketplace'}
              state={listing.cardHref ? backState : undefined}
              variant="ghost"
            >
              {listing.cardHref ? 'See all listings for this card' : 'Back to marketplace'}
            </Button>
          }
        />
      </MarketShell>
    );
  }

  const soldOut = !listing.quantity;
  // A listing already fully in the cart is not sold out and must not say so.
  // It says the true thing: you have all of it.
  const remaining = Math.max(0, (listing.quantity || 0) - inCartQuantity);
  const atMax = !soldOut && remaining <= 0;

  const ctaLabel = adding
    ? 'Adding'
    : soldOut
      ? 'Sold out'
      : atMax
        ? 'All available in your cart'
        : 'Add to cart';

  const specs = [
    { label: 'Set', value: listing.set },
    { label: 'Number', value: listing.cardNumber },
    { label: 'Rarity', value: listing.rarity },
    { label: 'Condition', value: listing.condition },
    /* Directly under Condition, because the two together are what a buyer is
       actually choosing between when several sellers list one card. Always
       printed when the API sent one, "Normal" included: this is a labelled
       list of facts about THIS listing, and a missing row reads as unknown
       rather than ordinary. `specs` already drops rows whose value is falsy,
       so `finishLabel`'s null omits the row for a listing with no finish. */
    { label: 'Finish', value: finishLabel(listing.finish) },
    { label: 'Available', value: listing.quantity ? `${listing.quantity} in stock` : 'Sold out' },
    {
      label: 'Dispatch',
      value: listing.processingDays ? `${listing.processingDays} days to dispatch` : null,
    },
  ];

  const priceLead = (
    <PriceLead
      label="This listing"
      price={formatCurrency(listing.price)}
      sub={
        soldOut
          ? 'Sold out'
          : `${listing.quantity} available from ${seller.storeName || seller.name || 'this seller'}`
      }
    />
  );

  return (
    <MarketShell
      {...shell}
      contentClassName={isDesktop ? undefined : 'dsa-detail-page--affixed'}
    >
      <DetailLayout>
        <DetailCol>
          <Gallery
            images={listing.images || []}
            index={imageIndex}
            onSelect={setImageIndex}
            alt={listing.name || 'Card'}
            badge={listing.condition}
          />

          <section>
            <h2 className="dsk-section-title">Sold by</h2>
            <SellerBox
              name={seller.name}
              storeName={seller.storeName}
              avatar={seller.avatar}
              rating={seller.rating}
              sales={seller.sales}
              verified={seller.verified}
              to={seller.id ? `/seller/${seller.id}` : undefined}
            />
          </section>

          {listing.description ? (
            <section>
              <h2 className="dsk-section-title">About this listing</h2>
              <Prose>
                <p>{listing.description}</p>
              </Prose>
            </section>
          ) : null}
        </DetailCol>

        <DetailAside>
          <Identity
            set={listing.set}
            name={listing.name}
            rarity={listing.rarity}
            condition={listing.condition}
            cardNumber={listing.cardNumber}
          />

          <MarketLine
            value={listing.marketPrice == null ? null : formatCurrency(listing.marketPrice)}
          />

          <PriceBox>
            {priceLead}
            {/* The chart lost its name when the tab labelled "Price history"
                went, so the name is printed above it. An unlabelled line of
                Rands over a date axis is a shape, not a fact. */}
            <div>
              <FieldLabel>Price history</FieldLabel>
              <PriceHistory
                marketHistory={priceHistory}
                height={200}
                formatValue={formatCurrency}
              />
            </div>
          </PriceBox>

          <CtaStack>
            {/* Desktop keeps the whole close in the sticky box. Mobile gets the
                pinned bar below instead, so the quantity control stays here and
                the bar carries only price and the one action. */}
            {!soldOut ? (
              <QtyRow note={`of ${listing.quantity} available`}>
                <QtyStepper
                  value={quantity}
                  onChange={onQuantityChange}
                  min={1}
                  max={Math.max(1, remaining)}
                  editable
                />
              </QtyRow>
            ) : null}

            {isDesktop ? (
              <Button
                size="lg"
                block
                disabled={adding || soldOut || atMax}
                onClick={() => onAddToCart && onAddToCart(quantity)}
              >
                {ctaLabel}
              </Button>
            ) : null}

            <Button variant="ghost" size="lg" block disabled={saving} onClick={onToggleSave}>
              <Icon as={Heart} size="sm" active={saved} />
              {saved ? 'In your collection' : 'Add to collection'}
            </Button>

            {onShare ? (
              <Button variant="ghost" size="lg" block onClick={onShare}>
                <Icon as={ShareNetwork} size="sm" />
                Share
              </Button>
            ) : null}

            {listing.cardHref ? (
              <CtaNote>
                Not the copy you want?{' '}
                {/* Same destination as the back control three lines of chrome
                    away, so it climbs the same way and carries the same
                    origin. A raw <a> here reloaded the whole app to reach a
                    page the router already had. */}
                <Link to={listing.cardHref} state={backState}>
                  Compare every seller with this card.
                </Link>
              </CtaNote>
            ) : null}
          </CtaStack>

          <SpecList items={specs} />
          <TrustRows shipsNationwide={shipsNationwide} />
        </DetailAside>
      </DetailLayout>

      {sellerListings.length > 0 ? (
        <DetailSection>
          <h2 className="dsk-section-title">
            More from {seller.storeName || seller.name || 'this seller'}
          </h2>
          <ListingGrid>
            {sellerListings.map((item) => (
              <ListingCard
                key={item.id}
                href={item.href}
                image={item.image}
                imageAlt={item.name}
                set={item.set}
                name={item.name}
                rarity={item.rarity}
                condition={item.condition}
                cardNumber={item.cardNumber}
                price={item.price == null ? undefined : formatCurrency(item.price)}
              />
            ))}
          </ListingGrid>
        </DetailSection>
      ) : null}

      {related.length > 0 ? (
        <DetailSection>
          <h2 className="dsk-section-title">Customers also purchased</h2>
          <ListingGrid>
            {related.map((item) => (
              <ListingCard
                key={item.id}
                href={item.href}
                image={item.image}
                imageAlt={item.name}
                set={item.set}
                name={item.name}
                rarity={item.rarity}
                cardNumber={item.cardNumber}
                price={item.price == null ? undefined : formatCurrency(item.price)}
                marketPrice={item.marketPrice == null ? undefined : formatCurrency(item.marketPrice)}
              />
            ))}
          </ListingGrid>
        </DetailSection>
      ) : null}

      {/* The pinned bar mounts only on the mobile track. See the block comment. */}
      {!isDesktop ? (
        <BuyAffix>
          <BuyBar
            price={formatCurrency(listing.price)}
            /* One line, three facts, and the finish only when it earns the
               room: this bar is pinned over the page on a phone and the
               condition and stock count are what a buyer needs to commit. */
            meta={
              soldOut
                ? 'Sold out'
                : [
                    listing.condition || 'Condition unknown',
                    distinguishingFinish(listing.finish),
                    `${listing.quantity} available`,
                  ]
                    .filter(Boolean)
                    .join(' · ')
            }
            cta={
              <Cta
                size="lg"
                disabled={adding || soldOut || atMax}
                onClick={() => onAddToCart && onAddToCart(quantity)}
              >
                {ctaLabel}
              </Cta>
            }
          />
        </BuyAffix>
      ) : null}

      {shareModal}
    </MarketShell>
  );
}

export default ListingDetailView;
